import { useEffect, useId, useState, type FormEvent } from 'react';
import { ENTITY_TYPE_NAMES, SHEET_NAME_MAX, javaStrip, type TemplateChoice } from '@realmery/realm-core';
import { RealmClientError, realms } from '../../db/client.ts';
import type { SheetEntryChoice } from '../../db/protocol.ts';

/**
 * New sheet, as the desktop's New Character Sheet dialog: a template (the realm's own, then
 * the built-ins - one is copied into the realm the first time a sheet uses it), its
 * description and licence, the character's name, and the entry it belongs to.
 */

export const choiceName = (choice: TemplateChoice) => (choice.kind === 'realm' ? choice.template.name : choice.builtIn.name);
const choiceSystem = (choice: TemplateChoice) => (choice.kind === 'realm' ? choice.template.system : `${choice.builtIn.system} · built in`);
const choiceKey = (choice: TemplateChoice) => (choice.kind === 'realm' ? `realm:${choice.template.id}` : `builtIn:${choice.builtIn.key}`);

export const entryChoiceLabel = (entry: SheetEntryChoice) =>
  `${entry.title} · ${entry.entityType === 'CUSTOM' && entry.customTypeName ? entry.customTypeName : (ENTITY_TYPE_NAMES[entry.entityType] ?? entry.entityType)}`;

export function NewSheet({
  file,
  forEntry,
  onCreated,
  onCancel,
}: {
  file: string;
  /** The entry the sheet is for (fixed, and it names the sheet), or null to choose one or none. */
  forEntry: { id: string; title: string } | null;
  onCreated: (sheetId: string) => void;
  onCancel: () => void;
}) {
  const [choices, setChoices] = useState<TemplateChoice[] | null>(null);
  const [entries, setEntries] = useState<SheetEntryChoice[]>([]);
  const [chosen, setChosen] = useState('');
  const [name, setName] = useState(forEntry?.title ?? '');
  const [entryId, setEntryId] = useState(forEntry?.id ?? '');
  const [error, setError] = useState('');
  const [working, setWorking] = useState(false);
  const ids = { name: useId(), entry: useId() };

  useEffect(() => {
    let live = true;
    void Promise.all([realms.templateChoices(file), forEntry ? Promise.resolve([]) : realms.sheetEntryChoices(file)]).then(([found, candidates]) => {
      if (!live) return;
      setChoices(found);
      setEntries(candidates);
      if (found[0]) setChosen(choiceKey(found[0]));
    });
    return () => {
      live = false;
    };
  }, [file, forEntry]);

  const choice = choices?.find((c) => choiceKey(c) === chosen) ?? null;
  const description = choice ? (choice.kind === 'realm' ? choice.template.description : choice.builtIn.description) : '';
  const license = choice ? (choice.kind === 'realm' ? choice.template.license : choice.builtIn.license) : '';

  function chooseEntry(id: string) {
    const old = entries.find((e) => e.id === entryId);
    const next = entries.find((e) => e.id === id);
    // The name follows the entry, unless it was typed.
    if (next && (name.trim() === '' || (old && name === old.title))) setName(next.title);
    setEntryId(id);
  }

  async function create(event: FormEvent) {
    event.preventDefault();
    if (!choice) return;
    setWorking(true);
    setError('');
    try {
      const template = choice.kind === 'realm' ? { templateId: choice.template.id } : { builtinKey: choice.builtIn.key };
      const { id } = await realms.createSheet(file, template, javaStrip(name), entryId || null);
      onCreated(id);
    } catch (e) {
      setError(e instanceof RealmClientError ? e.message : 'The sheet could not be made.');
      setWorking(false);
    }
  }

  return (
    <form className="panel new-sheet" aria-label="New character sheet" onSubmit={(e) => void create(e)}>
      <h2>{forEntry ? `A sheet for ${forEntry.title}` : 'New character sheet'}</h2>
      {choices === null ? (
        <p className="meta">Loading the templates…</p>
      ) : (
        <div className="new-sheet-body">
          <fieldset className="template-choices">
            <legend>Template</legend>
            {choices.map((c) => (
              <label key={choiceKey(c)} className="check template-choice">
                <input type="radio" name="sheet-template" checked={chosen === choiceKey(c)} onChange={() => setChosen(choiceKey(c))} />
                <span>
                  <strong>{choiceName(c)}</strong>
                  <span className="meta">{choiceSystem(c)}</span>
                </span>
              </label>
            ))}
          </fieldset>
          <div className="template-details">
            {description && <p>{description}</p>}
            {license && <p className="sheet-license">{license}</p>}
          </div>
        </div>
      )}
      <div className="row-fields">
        <label htmlFor={ids.name}>
          Name
          <input id={ids.name} value={name} maxLength={SHEET_NAME_MAX} placeholder="Character name" onChange={(e) => setName(e.currentTarget.value)} />
        </label>
        {!forEntry && (
          <label htmlFor={ids.entry}>
            Belongs to
            <select id={ids.entry} value={entryId} onChange={(e) => chooseEntry(e.currentTarget.value)}>
              <option value="">(No entry)</option>
              {entries.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entryChoiceLabel(entry)}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      {error && (
        <p className="notice-error" role="alert">
          {error}
        </p>
      )}
      <div className="actions">
        <button type="submit" className="primary" disabled={!choice || name === '' || working}>
          Create
        </button>
        <button type="button" onClick={onCancel} disabled={working}>
          Cancel
        </button>
      </div>
    </form>
  );
}
